import { ExternalLink, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import type { Media } from '@nucleus-commerce/api-client/admin';
import { isApiError, type ApiError } from '@nucleus-commerce/api-client/transport';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@nucleus-commerce/ui/components/alert-dialog';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@nucleus-commerce/ui/components/sheet';

import { MAX_MEDIA_ALT_LENGTH } from '@/shared/media/limits';

import { useDeleteMedia } from '../hooks/use-delete-media';
import { useMedia } from '../hooks/use-media';
import { useMediaFolders } from '../hooks/use-media-folders';
import { useUpdateMedia } from '../hooks/use-update-media';

import { FolderPickerDialog } from './folder-picker-dialog';
import { formatBytes } from './format-bytes';
import { MediaThumb } from './media-thumb';

export interface MediaDetailSheetProps {
  media: Media | null;
  onClose: () => void;
  /** Called after a successful delete, e.g. to drop the tile from a gallery. */
  onDeleted?: (id: string) => void;
}

const RENDITIONS = ['thumb', 'medium', 'large'] as const;

function AltForm({ media }: { media: Media }) {
  const { t } = useTranslation(['media', 'common']);
  const update = useUpdateMedia();
  const [alt, setAlt] = useState(media.alt ?? '');
  const dirty = alt.trim() !== (media.alt ?? '');
  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        update.mutate({ id: media.id, body: { alt: alt.trim() || null } });
      }}
    >
      <Label htmlFor="media-alt">{t('media:fields.alt')}</Label>
      <div className="flex gap-2">
        <Input
          id="media-alt"
          value={alt}
          onChange={(e) => setAlt(e.target.value)}
          placeholder={t('media:fields.altPlaceholder')}
          maxLength={MAX_MEDIA_ALT_LENGTH}
        />
        <Button type="submit" variant="outline" disabled={!dirty || update.isPending}>
          {t('common:actions.save')}
        </Button>
      </div>
      <p className="text-muted-foreground text-xs">{t('media:fields.altHint')}</p>
    </form>
  );
}

/** The folders the file sits in, with an editor; reads the detail cache so an edit shows at once. */
function FoldersSection({ media }: { media: Media }) {
  const { t } = useTranslation(['media', 'common']);
  const folders = useMediaFolders();
  const fresh = useMedia(media.id).data ?? media;
  const update = useUpdateMedia();
  const [open, setOpen] = useState(false);
  const names = fresh.folderIds
    .map((id) => folders.data?.find((folder) => folder.id === id))
    .filter((folder): folder is NonNullable<typeof folder> => folder !== undefined);
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{t('media:folders.inFolders')}</span>
        <Can permission="media.write" fallback={null}>
          <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(true)}>
            {t('media:folders.edit')}
          </Button>
        </Can>
      </div>
      {names.length > 0 ? (
        <ul className="flex flex-wrap gap-2" aria-label={t('media:folders.inFolders')}>
          {names.map((folder) => (
            <li key={folder.id}>
              <Badge variant="secondary">{folder.name}</Badge>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-muted-foreground text-xs">{t('media:folders.notInFolder')}</p>
      )}
      <FolderPickerDialog
        open={open}
        onOpenChange={setOpen}
        title={t('media:folders.editTitle')}
        description={t('media:folders.editDescription')}
        confirmLabel={t('common:actions.save')}
        initialSelected={new Set(fresh.folderIds)}
        pending={update.isPending}
        onConfirm={(folderIds) =>
          update.mutate({ id: media.id, body: { folderIds } }, { onSuccess: () => setOpen(false) })
        }
      />
    </div>
  );
}

/** The backend's MediaUsageKind values, in the order it reports them. */
const USAGE_KINDS = [
  'product_media',
  'product_featured',
  'variant',
  'content_block',
  'collection',
  'store_product_featured',
  'blog_post_featured',
  'swatch',
  'rich_text',
] as const;
type UsageKind = (typeof USAGE_KINDS)[number];

/** The kinds of record still pointing at the file, from the 409's `meta.usedBy`. */
function usedByKinds(error: ApiError | null): UsageKind[] {
  const list = error?.meta.usedBy;
  if (!Array.isArray(list)) return [];
  return USAGE_KINDS.filter((kind) => list.includes(kind));
}

function DeleteMedia({ media, onDeleted }: { media: Media; onDeleted: (id: string) => void }) {
  const { t } = useTranslation(['media', 'common']);
  const remove = useDeleteMedia();
  const message = useErrorMessage();
  const error: ApiError | null = isApiError(remove.error) ? remove.error : null;
  const usedBy = usedByKinds(error);
  return (
    <div className="flex flex-col gap-3">
      {error ? (
        <Alert variant="destructive">
          <AlertTitle>
            {error.code === 'RESOURCE_IN_USE' ? t('media:delete.inUseTitle') : message(error)}
          </AlertTitle>
          <AlertDescription>
            {error.code === 'RESOURCE_IN_USE' ? (
              <>
                <span>{t('media:delete.inUse')}</span>
                {usedBy.length > 0 ? (
                  <ul className="list-disc pl-4" aria-label={t('media:delete.usedBy')}>
                    {usedBy.map((kind) => (
                      <li key={kind}>{t(`media:usage.${kind}`)}</li>
                    ))}
                  </ul>
                ) : null}
              </>
            ) : null}
            {error.requestId ? (
              <span className="text-xs">
                {t('errors:requestIdShort', { requestId: error.requestId })}
              </span>
            ) : null}
          </AlertDescription>
        </Alert>
      ) : null}
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            variant="outline"
            className="text-destructive hover:text-destructive"
            disabled={remove.isPending}
          >
            <Trash2 aria-hidden />
            {t('media:delete.action')}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('media:delete.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('media:delete.description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => remove.mutate(media.id, { onSuccess: () => onDeleted(media.id) })}
            >
              {t('common:actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/** Side panel for one media row: preview, alt text, technical details, renditions, delete. */
export function MediaDetailSheet({ media, onClose, onDeleted }: MediaDetailSheetProps) {
  const { t } = useTranslation(['media', 'common']);
  const format = useFormat();
  return (
    <Sheet open={media !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-md">
        {media ? (
          <>
            <SheetHeader>
              <SheetTitle className="truncate">
                {media.alt ?? media.filename ?? t('media:untitled')}
              </SheetTitle>
              <SheetDescription>
                {t(`media:kind.${media.kind}`)} · {media.mimeType}
              </SheetDescription>
            </SheetHeader>
            <div className="flex flex-col gap-6 px-4 pb-6">
              <MediaThumb media={media} rendition="medium" className="w-full" />
              <Can permission="media.write" fallback={null}>
                <AltForm key={media.id} media={media} />
              </Can>
              <FoldersSection key={`folders-${media.id}`} media={media} />
              <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm">
                <dt className="text-muted-foreground">{t('media:fields.filename')}</dt>
                <dd className="truncate" title={media.filename ?? undefined}>
                  {media.filename ?? '—'}
                </dd>
                <dt className="text-muted-foreground">{t('media:fields.dimensions')}</dt>
                <dd className="tabular-nums">
                  {media.width && media.height ? `${media.width} × ${media.height}` : '—'}
                </dd>
                <dt className="text-muted-foreground">{t('media:fields.size')}</dt>
                <dd className="tabular-nums">{formatBytes(media.bytes)}</dd>
                <dt className="text-muted-foreground">{t('media:fields.uploaded')}</dt>
                <dd title={format.utc(media.createdAt)}>{format.dateTime(media.createdAt)}</dd>
                <dt className="text-muted-foreground">{t('media:fields.checksum')}</dt>
                <dd className="truncate font-mono text-xs" title={media.checksum}>
                  {media.checksum.slice(0, 16)}…
                </dd>
              </dl>
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium">{t('media:fields.renditions')}</span>
                {media.renditions ? (
                  <ul className="flex flex-wrap gap-2">
                    {RENDITIONS.map((name) => {
                      const url = media.renditions?.[name];
                      return url ? (
                        <li key={name}>
                          <Badge variant="outline" asChild>
                            <a href={url} target="_blank" rel="noreferrer">
                              {t(`media:rendition.${name}`)}
                              <ExternalLink aria-hidden />
                            </a>
                          </Badge>
                        </li>
                      ) : null;
                    })}
                  </ul>
                ) : (
                  <p className="text-muted-foreground text-xs">{t('media:renditionsPending')}</p>
                )}
                <a
                  href={media.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary inline-flex items-center gap-1 text-xs hover:underline"
                >
                  {t('media:openOriginal')}
                  <ExternalLink className="size-3" aria-hidden />
                </a>
              </div>
              <Can permission="media.write" fallback={null}>
                <DeleteMedia
                  media={media}
                  onDeleted={(id) => {
                    onDeleted?.(id);
                    onClose();
                  }}
                />
              </Can>
            </div>
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
